import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import { ThemeButton } from '../components/ThemeWrapper';
import SearchableSelect from '../components/Form/SearchableSelect';
import { accessAPI } from '../services/api';
import usePermissions from '../hooks/usePermissions';
import { isPrivilegedStaffRole } from '../utils/permissions';
import usePmaUploaders from '../hooks/usePmaUploaders';
import PmaUploadersPanel from '../components/PmaUploadersPanel';
import useCreationRequestNotifiers from '../hooks/useCreationRequestNotifiers';
import CreationRequestNotifiersPanel from '../components/CreationRequestNotifiersPanel';

const STAFF_ROLES = ['admin', 'trainer', 'recruiter', 'staff'];

const AdminAccessManagementPage = () => {
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const role = user?.role || user?.user?.role;

  const [tab, setTab] = useState('levels');
  const [permissions, setPermissions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingLevel, setEditingLevel] = useState(null);
  const [levelForm, setLevelForm] = useState({ name: '', description: '', permissions: [] });
  const [expandedUserId, setExpandedUserId] = useState(null);

  const permissionsByGroup = useMemo(() => {
    const groups = {};
    permissions.forEach((p) => {
      if (!groups[p.group]) groups[p.group] = [];
      groups[p.group].push(p);
    });
    return groups;
  }, [permissions]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [permRes, levelsRes, usersRes] = await Promise.all([
        accessAPI.getPermissions(),
        accessAPI.getLevels(),
        accessAPI.getUsers(),
      ]);
      setPermissions(permRes.data.permissions || []);
      setLevels(levelsRes.data.levels || []);
      setUsers(usersRes.data.users || []);
      setOrganizations(usersRes.data.organizations || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load access management data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    const usersRes = await accessAPI.getUsers();
    setUsers(usersRes.data.users || []);
  }, []);

  const pmaUploaders = usePmaUploaders(users, refreshUsers);
  const requestNotifiers = useCreationRequestNotifiers();
  const canManagePmaAccess = role === 'superadmin';
  const assignableLevels = useMemo(() => {
    if (canManagePmaAccess) return levels;
    return levels.filter((level) => !(level.permissions || []).includes('import.pma'));
  }, [levels, canManagePmaAccess]);

  useEffect(() => {
    if (role === 'student') {
      navigate('/dashboard');
      return;
    }
    const allowed =
      isPrivilegedStaffRole(role) ||
      (user?.permissions || user?.user?.permissions || []).includes('access.manage');
    if (!allowed) {
      navigate('/dashboard');
      return;
    }
    loadAll();
  }, [role, user, navigate, loadAll]);

  useEffect(() => {
    if (tab === 'pma' && !canManagePmaAccess) {
      setTab('levels');
    }
  }, [tab, canManagePmaAccess]);

  const resetLevelForm = () => {
    setEditingLevel(null);
    setLevelForm({ name: '', description: '', permissions: [] });
  };

  const startCreateLevel = () => {
    resetLevelForm();
    setTab('levels');
  };

  const startEditLevel = (level) => {
    setEditingLevel(level);
    setLevelForm({
      name: level.name,
      description: level.description || '',
      permissions: [...(level.permissions || [])],
    });
    setTab('levels');
  };

  const togglePermission = (key) => {
    setLevelForm((prev) => {
      const set = new Set(prev.permissions);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, permissions: [...set] };
    });
  };

  const saveLevel = async () => {
    setError(null);
    setSuccess(null);
    try {
      if (!levelForm.name.trim()) {
        setError('Access level name is required');
        return;
      }
      if (editingLevel) {
        await accessAPI.updateLevel(editingLevel.id, levelForm);
        setSuccess('Access level updated');
      } else {
        await accessAPI.createLevel(levelForm);
        setSuccess('Access level created');
      }
      resetLevelForm();
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save access level');
    }
  };

  const deleteLevel = async (level) => {
    if (level.isSystem) return;
    if (!window.confirm(`Delete access level "${level.name}"?`)) return;
    setError(null);
    try {
      await accessAPI.deleteLevel(level.id);
      setSuccess('Access level deleted');
      if (editingLevel?.id === level.id) resetLevelForm();
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete access level');
    }
  };

  const updateUserRow = async (userId, patch) => {
    setError(null);
    setSuccess(null);
    try {
      await accessAPI.updateUser(userId, patch);
      setSuccess('User access updated');
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const toggleUserExtraPermission = (staffUser, key) => {
    const current = new Set(staffUser.extraPermissions || []);
    if (current.has(key)) current.delete(key);
    else current.add(key);
    updateUserRow(staffUser.id, { extraPermissions: [...current] });
  };

  if (role === 'student' || !hasPermission('access.manage')) return null;

  return (
    <StaffPageShell
      title="Access Management"
      subtitle="Create access levels and assign them to staff, or grant special permissions to individuals."
    >
        <div className="profile-card theme-animate p-6">
          <p className="text-sm theme-text-secondary mb-6">
            Example: give <strong>Filter Students</strong> alone for view-only access — Edit/Delete
            stay hidden until you also grant <strong>Edit Students</strong> /{' '}
            <strong>Delete Students</strong>.
          </p>

          {error && (
            <div
              className="mb-4 p-3 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}
            >
              {error}
            </div>
          )}
          {success && (
            <div
              className="mb-4 p-3 rounded-lg text-sm"
              style={{ backgroundColor: 'var(--color-success-100)', color: 'var(--color-success)' }}
            >
              {success}
            </div>
          )}

          <div className="flex gap-2 mb-6 border-b pb-2" style={{ borderColor: 'var(--color-border)' }}>
            <button
              type="button"
              className={`px-4 py-2 rounded-t-lg font-medium ${tab === 'levels' ? 'theme-text-gradient' : 'theme-text-secondary'}`}
              style={tab === 'levels' ? { borderBottom: '2px solid var(--color-primary)' } : {}}
              onClick={() => setTab('levels')}
            >
              Access Levels
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-t-lg font-medium ${tab === 'users' ? 'theme-text-gradient' : 'theme-text-secondary'}`}
              style={tab === 'users' ? { borderBottom: '2px solid var(--color-primary)' } : {}}
              onClick={() => setTab('users')}
            >
              User Assignments
            </button>
            {canManagePmaAccess && (
              <button
                type="button"
                className={`px-4 py-2 rounded-t-lg font-medium ${tab === 'pma' ? 'theme-text-gradient' : 'theme-text-secondary'}`}
                style={tab === 'pma' ? { borderBottom: '2px solid var(--color-primary)' } : {}}
                onClick={() => setTab('pma')}
              >
                PMA uploaders
              </button>
            )}
            <button
              type="button"
              className={`px-4 py-2 rounded-t-lg font-medium ${tab === 'requests' ? 'theme-text-gradient' : 'theme-text-secondary'}`}
              style={tab === 'requests' ? { borderBottom: '2px solid var(--color-primary)' } : {}}
              onClick={() => setTab('requests')}
            >
              Account request alerts
            </button>
          </div>

          {loading ? (
            <p className="theme-text">Loading…</p>
          ) : tab === 'pma' && canManagePmaAccess ? (
            <PmaUploadersPanel
              users={users}
              busyId={pmaUploaders.busyId}
              error={pmaUploaders.error}
              selectedCount={pmaUploaders.selectedCount}
              onToggle={pmaUploaders.setPmaEnabled}
            />
          ) : tab === 'requests' ? (
            <CreationRequestNotifiersPanel
              staff={requestNotifiers.staff}
              selectedCount={requestNotifiers.selectedCount}
              busyId={requestNotifiers.busyId}
              loading={requestNotifiers.loading}
              error={requestNotifiers.error}
              onToggle={requestNotifiers.setNotified}
            />
          ) : tab === 'levels' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold theme-text">Existing levels</h2>
                  <ThemeButton type="button" onClick={startCreateLevel}>
                    + New level
                  </ThemeButton>
                </div>
                <ul className="space-y-2">
                  {levels.map((level) => (
                    <li
                      key={level.id}
                      className="p-3 rounded-lg border flex justify-between items-start gap-2"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div>
                        <div className="font-medium theme-text">
                          {level.name}
                          {level.isSystem && (
                            <span
                              className="ml-2 text-xs px-2 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--color-primary-100)' }}
                            >
                              system
                            </span>
                          )}
                        </div>
                        {level.description && (
                          <p className="text-sm theme-text-secondary mt-1">{level.description}</p>
                        )}
                        <p className="text-xs theme-text-secondary mt-1">
                          {(level.permissions || []).length} permissions · {level._count?.users ?? 0}{' '}
                          users
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          type="button"
                          className="text-sm theme-text"
                          onClick={() => startEditLevel(level)}
                        >
                          Edit
                        </button>
                        {!level.isSystem && (
                          <button
                            type="button"
                            className="text-sm"
                            style={{ color: 'var(--color-error)' }}
                            onClick={() => deleteLevel(level)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                <h2 className="text-lg font-semibold theme-text mb-4">
                  {editingLevel ? `Edit: ${editingLevel.name}` : 'Create access level'}
                </h2>
                <label className="block mb-3">
                  <span className="text-sm theme-text-secondary">Name</span>
                  <input
                    className="w-full mt-1 px-3 py-2 rounded border theme-text"
                    style={{
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-background)',
                    }}
                    value={levelForm.name}
                    onChange={(e) => setLevelForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={editingLevel?.isSystem}
                  />
                </label>
                <label className="block mb-4">
                  <span className="text-sm theme-text-secondary">Description</span>
                  <textarea
                    className="w-full mt-1 px-3 py-2 rounded border theme-text"
                    style={{
                      borderColor: 'var(--color-border)',
                      backgroundColor: 'var(--color-background)',
                    }}
                    rows={2}
                    value={levelForm.description}
                    onChange={(e) => setLevelForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </label>
                <p className="text-sm font-medium theme-text mb-2">Permissions</p>
                <div className="max-h-80 overflow-y-auto space-y-4 mb-4">
                  {Object.entries(permissionsByGroup).map(([group, items]) => (
                    <div key={group}>
                      <p className="text-xs font-semibold theme-text-secondary uppercase mb-2">
                        {group}
                      </p>
                      <div className="space-y-1">
                        {items.map((p) => (
                          <label
                            key={p.key}
                            className="flex items-center gap-2 text-sm theme-text cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={levelForm.permissions.includes(p.key)}
                              onChange={() => togglePermission(p.key)}
                            />
                            {p.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <ThemeButton type="button" onClick={saveLevel}>
                    {editingLevel ? 'Update level' : 'Create level'}
                  </ThemeButton>
                  {editingLevel && (
                    <button
                      type="button"
                      className="px-4 py-2 theme-text-secondary"
                      onClick={resetLevelForm}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm theme-text-secondary">
                Change <strong>role</strong> away from admin to use access levels. Then assign a
                level and optionally add <strong>special permissions</strong> on top.
                Use role <strong>staff</strong> for granular-only access (no trainer/recruiter
                baseline) — e.g. assign level &quot;Mock Interviews only&quot;.
              </p>
              {users.map((u) => {
                const isExpanded = expandedUserId === u.id;
                const levelPerms = new Set(u.accessLevel?.permissions || []);
                const extras = u.extraPermissions || [];
                const isSelf = String(u.id) === String(user?.id || user?.user?.id || '');
                const isAdminRole = isPrivilegedStaffRole(u.role);
                const isSuperadmin = u.role === 'superadmin';
                return (
                  <div
                    key={u.id}
                    className="rounded-lg border"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    <div className="p-3 flex flex-wrap items-center gap-3">
                      <div className="min-w-[180px] flex-1">
                        <div className="font-medium theme-text">
                          {u.firstName} {u.lastName}
                        </div>
                        <div className="text-xs theme-text-secondary">{u.email}</div>
                        {organizations.length > 0 && (
                          <div className="flex flex-wrap gap-3 mt-2">
                            {organizations.map((org) => {
                              const selected = (u.organizationIds || []).includes(org.id);
                              return (
                                <label key={org.id} className="flex items-center gap-1 text-xs theme-text cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => {
                                      const current = new Set(u.organizationIds || []);
                                      if (selected) current.delete(org.id);
                                      else current.add(org.id);
                                      const grantable = new Set(organizations.map((item) => item.id));
                                      updateUserRow(u.id, {
                                        organizationIds: [...current].filter((id) => grantable.has(id)),
                                      });
                                    }}
                                  />
                                  {org.name}
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {isSuperadmin && (
                          <div className="text-xs mt-1 theme-text-secondary">
                            Superadmin has full access — role cannot be changed here
                          </div>
                        )}
                        {isAdminRole && !isSuperadmin && (
                          <div className="text-xs mt-1 theme-text-secondary">
                            Admin has full access — change role to trainer/recruiter/staff for
                            granular levels
                          </div>
                        )}
                        {u.role === 'staff' && (
                          <div className="text-xs mt-1 theme-text-secondary">
                            Staff has no baseline — permissions come only from the access level
                            and specials
                          </div>
                        )}
                        {!isAdminRole && extras.length > 0 && (
                          <div className="text-xs mt-1" style={{ color: 'var(--color-primary)' }}>
                            +{extras.length} special permission{extras.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <div className="min-w-[120px]">
                        <SearchableSelect
                          value={u.role}
                          disabled={isSuperadmin || (isSelf && isAdminRole)}
                          required
                          onChange={(e) => {
                            const nextRole = e.target.value;
                            if (
                              isAdminRole &&
                              nextRole !== 'admin' &&
                              !window.confirm(
                                `Remove admin from ${u.firstName || u.email}? They will use access levels (and role baselines for trainer/recruiter) instead of full admin access.`
                              )
                            ) {
                              return;
                            }
                            updateUserRow(u.id, { role: nextRole });
                          }}
                          options={(isSuperadmin ? ['superadmin'] : STAFF_ROLES).map((r) => ({
                            value: r,
                            label: r,
                          }))}
                          aria-label={`Role for ${u.email}`}
                        />
                        {isSelf && isAdminRole && (
                          <p className="text-[10px] theme-text-secondary mt-1">
                            Another admin must change your role
                          </p>
                        )}
                      </div>
                      <div className="min-w-[160px]">
                        <SearchableSelect
                          value={u.accessLevelId || ''}
                          placeholder="— None —"
                          disabled={isAdminRole}
                          onChange={(e) =>
                            updateUserRow(u.id, {
                              accessLevelId: e.target.value || null,
                            })
                          }
                          options={assignableLevels}
                          getOptionValue={(level) => level.id}
                          getOptionLabel={(level) => level.name}
                          aria-label={`Access level for ${u.email}`}
                        />
                      </div>
                      <label className="flex items-center gap-1 text-sm theme-text">
                        <input
                          type="checkbox"
                          checked={u.isActive}
                          onChange={(e) => updateUserRow(u.id, { isActive: e.target.checked })}
                        />
                        Active
                      </label>
                      {!isAdminRole && (
                        <button
                          type="button"
                          className="text-sm px-3 py-1 rounded border theme-text"
                          style={{ borderColor: 'var(--color-border)' }}
                          onClick={() => setExpandedUserId(isExpanded ? null : u.id)}
                        >
                          {isExpanded ? 'Hide special access' : 'Special access'}
                        </button>
                      )}
                    </div>
                    {isExpanded && !isAdminRole && (
                      <div
                        className="px-4 pb-4 border-t"
                        style={{
                          borderColor: 'var(--color-border)',
                          backgroundColor: 'var(--color-surface)',
                        }}
                      >
                        <p className="text-sm theme-text mt-3 mb-2">
                          Special permissions for this user only (in addition to{' '}
                          <strong>{u.accessLevel?.name || 'no level'}</strong>):
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 max-h-64 overflow-y-auto py-1 px-0.5">
                          {permissions.map((p) => {
                            const fromLevel = levelPerms.has(p.key);
                            const fromExtra = extras.includes(p.key);
                            return (
                              <label
                                key={p.key}
                                className={`flex items-start gap-2 text-sm theme-text min-h-[1.75rem] ${
                                  fromLevel ? 'opacity-60' : 'cursor-pointer'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="mt-1 shrink-0"
                                  checked={fromLevel || fromExtra}
                                  disabled={fromLevel}
                                  onChange={() => toggleUserExtraPermission(u, p.key)}
                                />
                                <span className="leading-snug">
                                  {p.label}
                                  {fromLevel && (
                                    <span className="block text-xs theme-text-secondary mt-0.5">
                                      Already from access level
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
    </StaffPageShell>
  );
};

export default AdminAccessManagementPage;
